import { createApp, h } from "vue"
import { Amplify } from "aws-amplify"
import App from "./App.vue"
import AppAlert from "./components/AppAlert.vue"
import filters from "./filters"
import { i18n } from "./plugins/i18n"
import vuetify from "./plugins/vuetify"
import router from "./router"
import { store } from "./store"
import pinDirective from "./utils/pin-directive"
import LanguageUtils from "./utils/languages"

const DEFAULT_LANG = LanguageUtils.defaultLang().code

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VUE_APP_AWS_USER_POOL_ID,
      userPoolClientId: import.meta.env.VUE_APP_AWS_USER_POOL_WEB_CLIENT_ID,
      identityPoolId: import.meta.env.VUE_APP_IDENTITY_POOL_ID
    }
  },

  Storage: {
    S3: {
      bucket: import.meta.env.VUE_APP_S3_STORAGE_BUCKET,
      region: import.meta.env.VUE_APP_AWS_PROJECT_REGION
    }
  }
})

const app = createApp({
  render: () => h(App),
  computed: {
    isUserLoggedIn() {
      return this.$store.getters.authToken !== null
    }
  },
  created() {
    if (this.$store.getters.loggedInUser) {
      this.fetchData()
    }
  },
  methods: {
    setLanguage(langCode) {
      langCode = langCode || DEFAULT_LANG
      this.$store.dispatch("setLanguage", langCode)

      if (this.$store.getters.userDetails && this.$store.getters.loggedInUser) {
        if (
          (this.$store.getters.userDetails.preferences?.language ||
            DEFAULT_LANG) != langCode
        ) {
          this.$store
            .dispatch("updateUserPreferences", { language: langCode })
            .catch(() => {
              // Error already displayed by dispatched action.
            })
        }
      }
    },
    async fetchData() {
      if (this.$route.query.logout) {
        return //Do not load anything if we are going to logout
      }

      const dataLangCode = this.$store.getters.dataLangCode

      this.$store.dispatch("loadEventMetadata", dataLangCode)
      this.$store.dispatch("loadMeasurementMetadata", dataLangCode)
      this.$store.dispatch("loadProductMetadata", dataLangCode)
      this.$store.dispatch("loadProductGroupMetadata", dataLangCode)

      // Only load parameter metadata if the user can read parameters.
      if (this.$store.getters.canReadParameters) {
        this.$store.dispatch("loadParameterMetadata", dataLangCode)
      }

      // Only load role metadata if the user can read users.
      if (this.$store.getters.canReadUsers) {
        this.$store.dispatch("loadRoleMetadata", dataLangCode)
      }

      let tenantTreeLoaded = true
      try {
        await this.$store.dispatch("loadTenantTree")
      } catch {
        // Error already displayed by dispatched action.
        tenantTreeLoaded = false
      }
      if (!tenantTreeLoaded) {
        return // We can't load anything more when tenant tree not loaded properly
      }

      this.$store.dispatch("fetchMachines", this.$store.getters.tenantRoot.uid)

      await this.$store.dispatch("loadMessages", dataLangCode)

      await this.$store.dispatch("fetchUserDetails", {
        tenantUid: this.$store.getters.loggedInUser.tenantUid,
        uid: this.$store.getters.loggedInUser.uid
      })
      this.setLanguage(this.$store.getters.langCode || DEFAULT_LANG)
    }
  },
  watch: {
    isUserLoggedIn() {
      if (this.$store.getters.loggedInUser) {
        this.fetchData()
      }
    }
  }
})
app.use(router)
app.use(store)
app.use(vuetify)
app.use(i18n)

app.component("app-alert", AppAlert)

app.directive("pin", pinDirective)

app.config.globalProperties.$filters = filters

app.mount("#app")
